import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import type { PublicGameState } from "@grundbesitz/shared";
import { minimumBid } from "@grundbesitz/engine";
import { actionError, bidCheck, parseBid, quickBids } from "../../../lib/assets";
import type { BidCheck } from "../../../lib/assets";
import { formatMoney } from "../../../lib/format";
import { Button } from "../../ui/Button";
import { useAct } from "../assets/useAct";

/** Bid input, quick buttons and "Verzichten". Bids go out with `skipVersion`, the engine judges the amount. */
export function BidControls({ game, pid, autoFocus }: { game: PublicGameState; pid: string; autoFocus: boolean }) {
  const uid = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const { busy, run } = useAct();

  const me = game.players.find((p) => p.id === pid)!;
  const min = minimumBid(game);
  const empty = text.trim() === "";
  const typed = parseBid(text);
  const target = typed ?? min;
  const check: BidCheck = !empty && typed === null ? { ok: false, message: "Gib das Gebot als ganze Zahl ein." } : bidCheck(game, pid, target);
  const quick = quickBids(game, me.cash);
  // Blockers that do not depend on the amount (debt, already leading, already passed) lock everything.
  const base = bidCheck(game, pid, min);
  const locked = !base.ok && !(base.message ?? "").startsWith("Du hast ");
  const passError = actionError(game, pid, { type: "PASS_AUCTION" });

  useEffect(() => {
    if (!autoFocus) return;
    // Sheet moves focus to the dialog in an effect of its own, which runs after this one.
    const id = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(id);
  }, [autoFocus]);

  const bid = async (amount: number) => {
    setServerError(null);
    const ack = await run(`bid-${amount}`, { type: "BID", amount }, { skipVersion: true });
    if (ack.ok) setText("");
    else setServerError(ack.message);
    inputRef.current?.focus();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (check.ok && !locked) void bid(target);
  };

  const hint = serverError ?? (locked ? base.message : check.message);
  const isError = serverError !== null || locked || (!empty && !check.ok);

  return (
    <div className="space-y-3">
      <div
        className="grid gap-1.5"
        style={{ gridTemplateColumns: `repeat(${quick.length}, minmax(0, 1fr))` }}
        role="group"
        aria-label="Schnellgebote"
      >
        {quick.map((q) => (
          <Button
            key={q.label}
            variant="secondary"
            className="px-1 tabular-nums"
            disabled={locked || !q.affordable || busy !== null}
            aria-label={`Bieten ${formatMoney(q.amount)}`}
            title={formatMoney(q.amount)}
            onClick={() => void bid(q.amount)}
          >
            {q.label}
          </Button>
        ))}
      </div>

      <form onSubmit={submit} className="flex items-stretch gap-2">
        <div className="relative min-w-0 flex-1">
          <label htmlFor={`${uid}-bid`} className="sr-only">
            Dein Gebot in M
          </label>
          <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-2">
            M
          </span>
          <input
            ref={inputRef}
            id={`${uid}-bid`}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            enterKeyHint="send"
            placeholder={`ab ${min}`}
            value={text}
            disabled={locked}
            aria-describedby={`${uid}-hint`}
            aria-invalid={!empty && !check.ok}
            onChange={(e) => {
              setText(e.target.value);
              setServerError(null);
            }}
            className="min-h-12 w-full rounded-lg border-2 border-line bg-bg pl-8 pr-3 text-right text-lg font-semibold tabular-nums focus:border-accent disabled:opacity-50"
          />
        </div>
        <Button type="submit" variant="primary" className="min-h-12 px-5 text-base" disabled={!check.ok || locked || busy !== null}>
          Bieten{check.ok && !locked ? ` ${formatMoney(target)}` : ""}
        </Button>
      </form>

      <p
        id={`${uid}-hint`}
        className={`min-h-5 text-sm ${isError ? "font-semibold text-danger" : "text-ink-2"}`}
      >
        {hint ?? `Du hast ${formatMoney(me.cash)}.`}
      </p>

      <Button
        variant="ghost"
        className="w-full"
        disabled={passError !== null || busy !== null}
        title={passError ?? undefined}
        onClick={() => void run("pass", { type: "PASS_AUCTION" }, { skipVersion: true })}
      >
        Verzichten
      </Button>
    </div>
  );
}
